import React, { useState } from "react";
import BetaForm from "../../beta-form";
import { useModals } from "../../hooks/useModals";
import Link from "next/link";
import { StoreFrontLogo } from "./header";
import { socialsDataMap } from "@/components/storefronts-v1/modals/details";

interface Props {
  mode?: "light" | "dark";
  storeName: string;
  hasPaidSubscription: boolean;
  toggle: (key: string) => void;
  socials: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
  };
  refund_policy?: string;
  className?: string;
  logo: string | null;
}

const StoreFooter: React.FC<Props> = ({
  mode = "light",
  socials,
  storeName,
  refund_policy,
  hasPaidSubscription,
  className,
  toggle,
  logo,
}) => {
  const socialsData = Object.entries(socials ?? {})
    .filter((e) => !!e?.[1])
    .map((e) => ({ type: e[0], handle: e[1] }));

  const [showMorePolicy, setShowMorePolicy] = useState(false);
  const renderModalButtons = [
    { key: "about_store", label: "About us" },
    { key: "delivery_fees", label: "Delivery Fees" },
    { key: "refund_policy", label: "Return and Refund Policy" },
    { key: "more_info", label: "More Information" },
  ];

  return (
    <section className="bg-grey-fields-100">
      <footer
        className={`sm:px-9 md:px-12.5 pb-5 sm:pb-7.5 pt-6.75 sm:pt-8 justify-between border-opacity-40 px-5 ${
          mode === "light" ? "border-grey-border" : "border-grey-divider"
        } ${className} `}
      >
        <div className="store-container">
          <div className="flex flex-col lg:flex-row gap-y-7 items-start justify-between">
            <div className="w-full md:w-1/2 lg:w-1/2">
              <div className="mt-0">
                <StoreFrontLogo logo={logo} storeName={storeName} />
              </div>

              {refund_policy && (
                <div className="mt-6.25">
                  <h3 className="font-bold text-base sm:text-lg text-black-secondary mb-1.25">Refund Policy</h3>
                  <div className="text-dark text-1xs lg:max-w-[700px] whitespace-pre-wrap ">
                    <span className={`${!showMorePolicy && "elipsis-2"}`}>{refund_policy}</span>
                    {
                      <button
                        onClick={() => setShowMorePolicy(!showMorePolicy)}
                        className="text-primary-500 block font-medium"
                      >
                        {!showMorePolicy ? "Show more" : "Hide more"}
                      </button>
                    }
                  </div>
                </div>
              )}
            </div>
            <div>
              <h2 className="text-black font-bold text-base sm:text-lg inline-flex items-center">Learn More</h2>

              <div className="mt-3 grid space-y-2 text-sm">
                {renderModalButtons.map((b, id) => (
                  <p key={id}>
                    <button className="inline-flex gap-x-2 text-dark text-1xs" onClick={() => toggle(b.key)}>
                      {b.label}
                    </button>
                  </p>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-black font-bold text-base sm:text-lg inline-flex items-center">
                Supported Payment Methods
              </h2>

              <div className="mt-3 grid space-y-2 text-sm">
                <p>
                  <button className="inline-flex items-center gap-x-2 text-dark text-1xs">
                    <CheckIcon /> Cards (Mastercard, Visa, Verve)
                  </button>
                </p>
                <p>
                  <button className="inline-flex items-center gap-x-2 text-dark text-1xs">
                    <CheckIcon /> Bank Transfers
                  </button>
                </p>
                <p>
                  <button className="inline-flex items-center gap-x-2 text-dark text-1xs">
                    <CheckIcon /> Direct Debit
                  </button>
                </p>
              </div>
            </div>
          </div>
          <div className="flex w-full items-center justify-between gap-8 flex-wrap pt-6.25 mt-6.25 border-t border-grey-border">
            <div className="flex items-center gap-4">
              {socialsData.map((s, index) => (
                <a
                  className="flex items-center text-primary-500"
                  key={index}
                  href={`${socialsDataMap[s.type].prefix}/${s.handle}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <figure className={`h-6 w-6 sm:h-6 sm:w-6 rounded-full flex items-center bg-grey-fields-100 py-1.5`}>
                    {socialsDataMap[s.type].icon}
                  </figure>
                </a>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <span
                  className={`text-sm font-bold font-display inline-block pr-0.5 mr-0.5 ${
                    mode === "light" ? "text-black" : "text-white"
                  }`}
                >
                  &copy; {new Date().getFullYear()}
                </span>
                <span
                  className={`text-sm font-bold font-display inline-block ${
                    mode === "light" ? "text-black" : "text-white"
                  }`}
                >
                  - {storeName}
                </span>
              </div>
              <a
                href={process.env.NEXT_PUBLIC_DASHBOARD_URL}
                target="_blank"
                rel="noreferrer"
                className="bg-white px-3.75 py-1.5 rounded-[100px] text-1xs text-black flex items-center font-medium space-x-1.5"
              >
                <span>Powered by</span>
                <img src="/images/logos/primary.svg" alt="Catlog logo" className="h-4" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </section>
  );
};

const CheckIcon = () => {
  return (
    <div className="bg-primary-500 rounded-full text-white p-1.5 md:p-1 flex items-center justify-center">
      <svg viewBox="0 0 11 8" fill="none" className=" h-[10.5px] w-[10.5px] md:h-[12.5px] md:w-[12.5px]">
        <path
          d="M10 1L3.8125 7L1 4.27273"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

export default StoreFooter;
